// Minimal Google OAuth Configuration and Authentication Service

export interface User {
  id: string
  email: string
  name: string
  picture?: string
  accessToken?: string
}

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

// Google OAuth Configuration - Minimal setup
export const GOOGLE_OAUTH_CONFIG = {
  clientId: '401457232394-v06p8u6kgsv61ev7hg9p4irt3hn0dhu1.apps.googleusercontent.com',
  scope: 'openid email profile'
}

// Allowed email domains for access control
export const ALLOWED_DOMAINS = [
  'architechlab.org',
  'architech.org',
  // Add other allowed domains as needed
]

export class AuthService {
  private static instance: AuthService
  private authState: AuthState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null
  }
  private listeners: ((state: AuthState) => void)[] = []

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  // Initialize Google OAuth - Minimal approach
  async initializeGoogleAuth(): Promise<void> {
    try {
      console.log('🔧 Debug: Initializing minimal Google Auth...')
      
      // Load Google Identity Services script
      await this.loadGoogleIdentityScript()
      
      // Initialize with minimal configuration
      if (window.google && window.google.accounts) {
        console.log('🔧 Debug: Google Identity Services available, initializing...')
        
        window.google.accounts.id.initialize({
          client_id: GOOGLE_OAUTH_CONFIG.clientId,
          callback: (response: any) => {
            console.log('🔧 Debug: OAuth callback received')
            this.handleGoogleResponse(response)
          }
        })
        
        console.log('🔧 Debug: Minimal OAuth initialized successfully')
      } else {
        console.error('🔧 Debug: Google Identity Services not available')
        throw new Error('Google Identity Services not available')
      }
    } catch (error) {
      console.error('🔧 Debug: Failed to initialize Google Auth:', error)
      throw error
    }
  }

  // Handle Google OAuth response
  private handleGoogleResponse(response: any): void {
    try {
      console.log('🔧 Debug: Processing Google response:', response)
      
      const user: User = {
        id: response.sub,
        email: response.email,
        name: response.name,
        picture: response.picture,
        accessToken: response.credential
      }

      console.log('🔧 Debug: User profile:', { email: user.email, name: user.name })

      if (!this.isEmailAllowed(user.email)) {
        console.log('🔧 Debug: Email domain not allowed:', user.email)
        this.setError('Access denied. Please use an authorized email domain.')
        return
      }

      console.log('🔧 Debug: Email domain allowed, setting user...')
      this.setUser(user)
      this.setAuthenticated(true)
      this.clearError()
      
    } catch (error) {
      console.error('🔧 Debug: Error processing Google response:', error)
      this.setError('Failed to process authentication response')
    }
  }

  // Load Google Identity Services script
  private loadGoogleIdentityScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.accounts) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'))
      document.head.appendChild(script)
    })
  }

  // Sign in with Google - Minimal approach
  async signInWithGoogle(): Promise<User> {
    try {
      console.log('🔧 Debug: Starting minimal Google Sign-In...')
      this.setLoading(true)
      
      if (!window.google || !window.google.accounts) {
        throw new Error('Google Identity Services not available')
      }

      // Use the simplest prompt method
      return new Promise((resolve, reject) => {
        window.google.accounts.id.prompt((notification: any) => {
          console.log('🔧 Debug: Prompt notification:', notification)
          
          if (notification.isNotDisplayed()) {
            const reason = notification.getNotDisplayedReason()
            console.log('🔧 Debug: Sign-in not displayed:', reason)
            reject(new Error(`Sign-in not displayed: ${reason}`))
            return
          }
          if (notification.isSkippedMoment()) {
            const reason = notification.getSkippedReason()
            console.log('🔧 Debug: Sign-in skipped:', reason)
            reject(new Error(`Sign-in skipped: ${reason}`))
            return
          }
          if (notification.isDismissedMoment()) {
            const reason = notification.getDismissedReason()
            console.log('🔧 Debug: Sign-in dismissed:', reason)
            reject(new Error(`Sign-in dismissed: ${reason}`))
            return
          }
          
          console.log('🔧 Debug: Sign-in prompt should be displayed')
        })

        // Set up callback to handle the response
        window.google.accounts.id.initialize({
          client_id: GOOGLE_OAUTH_CONFIG.clientId,
          callback: (response: any) => {
            console.log('🔧 Debug: OAuth callback received in sign-in')
            
            try {
              const user: User = {
                id: response.sub,
                email: response.email,
                name: response.name,
                picture: response.picture,
                accessToken: response.credential
              }

              console.log('🔧 Debug: User profile from sign-in:', { email: user.email, name: user.name })

              if (!this.isEmailAllowed(user.email)) {
                console.log('🔧 Debug: Email domain not allowed in sign-in:', user.email)
                reject(new Error('Access denied. Please use an authorized email domain.'))
                return
              }

              console.log('🔧 Debug: Email domain allowed in sign-in, setting user...')
              this.setUser(user)
              this.setAuthenticated(true)
              this.clearError()
              
              resolve(user)
            } catch (error) {
              console.error('🔧 Debug: Error processing sign-in response:', error)
              reject(error)
            } finally {
              this.setLoading(false)
            }
          }
        })
      })
    } catch (error) {
      console.error('🔧 Debug: Google Sign-In error:', error)
      this.setError(error instanceof Error ? error.message : 'Sign in failed')
      this.setLoading(false)
      throw error
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      console.log('🔧 Debug: Signing out...')
      this.setUser(null)
      this.setAuthenticated(false)
      this.clearError()
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  // Check if email domain is allowed
  private isEmailAllowed(email: string): boolean {
    const domain = email.split('@')[1]
    return ALLOWED_DOMAINS.includes(domain)
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.authState.user
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.authState.isAuthenticated
  }

  // Get auth state
  getAuthState(): AuthState {
    return { ...this.authState }
  }

  // Subscribe to auth state changes
  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.push(listener)
    listener(this.authState)
    
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener)
    }
  }

  // Private setters
  private setUser(user: User | null): void {
    this.authState.user = user
    this.notifyListeners()
  }

  private setAuthenticated(isAuthenticated: boolean): void {
    this.authState.isAuthenticated = isAuthenticated
    this.notifyListeners()
  }

  private setLoading(isLoading: boolean): void {
    this.authState.isLoading = isLoading
    this.notifyListeners()
  }

  private setError(error: string | null): void {
    this.authState.error = error
    this.notifyListeners()
  }

  private clearError(): void {
    this.authState.error = null
    this.notifyListeners()
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener({ ...this.authState }))
  }
}

// Export singleton instance
export const authService = AuthService.getInstance()

// Type declarations for Google API
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void
          renderButton: (element: HTMLElement, config: any) => void
          prompt: (callback?: (notification: any) => void) => void
          disableAutoSelect: () => void
          storeCredential: (credential: any) => void
          cancel: () => void
        }
      }
    }
  }
} 